import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo, useState} from 'react';
import {Panel, PanelGroup, PanelResizeHandle} from 'react-resizable-panels';

import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import {GRAY, GRAY_LIGHT, WHITE, hexToRgba} from 'ui-library/Colors';
import useOnMount from 'ui-library/hooks/useOnMount';

import type {ChatLayoutProps} from '../ChatLayoutRegistry';
import {AnalysisWorkspaceContext} from './AnalysisWorkspaceContext';
import type {PreviewContent} from './PreviewPane';
import {PreviewPane} from './PreviewPane';

/**
 * Workspace layout for Analysis chat.
 *
 * Shows: [Plan pane] | [Chat pane] | [Preview pane]
 *
 * - Plan pane: hidden until plan artifact appears from application state
 * - Chat pane: standard chat content
 * - Preview pane: hidden until user clicks a code block or "View code" on a chart
 *
 */
export function AnalysisWorkspaceLayout({chatContent, artifacts, isEditable}: ChatLayoutProps): JSX.Element {
  const {planArtifact} = artifacts;
  const [isPlanResizerDragging, setIsPlanResizerDragging] = useState(false);
  const [isPreviewResizerDragging, setIsPreviewResizerDragging] = useState(false);
  const [previewContent, setPreviewContent] = useState<PreviewContent | null>(null);

  useOnMount(() => {
    SidepanelActions.expand();
  });

  const openCode = useCallback((code: string, language: string, label: string) => {
    setPreviewContent({kind: 'code', code, language, label});
  }, []);

  const openFile = useCallback(
    (contentUrl: string, downloadUrl: string, filename: string, mimeType: string) => {
      setPreviewContent({kind: 'file', url: contentUrl, downloadUrl, filename, mimeType, label: filename});
    },
    []
  );

  const closePreview = useCallback(() => {
    setPreviewContent(null);
  }, []);

  const contextValue = useMemo(() => ({openCode, openFile}), [openCode, openFile]);

  const hasPlan = planArtifact != null;
  const hasPreview = previewContent != null;

  // Panel sizes must sum to 100 for visible panels
  let chatSize: number;
  let planSize: number;
  let previewSize: number;
  if (hasPlan && hasPreview) {
    planSize = 15;
    chatSize = 50;
    previewSize = 35;
  } else if (hasPlan) {
    planSize = 20;
    chatSize = 80;
    previewSize = 0;
  } else if (hasPreview) {
    planSize = 0;
    chatSize = 60;
    previewSize = 40;
  } else {
    planSize = 0;
    chatSize = 100;
    previewSize = 0;
  }

  return (
    <AnalysisWorkspaceContext.Provider value={contextValue}>
      <div className={css(styles.container)} aria-label="Analysis workspace">
        <PanelGroup direction="horizontal" style={{width: '100%', height: '100%'}}>
          {/* Plan panel */}
          {hasPlan && planArtifact && (
            <>
              <Panel
                id="plan"
                order={0}
                defaultSize={planSize}
                minSize={10}
                maxSize={35}
                className={css(styles.panel)}
              >
                <div role="region" aria-label="Plan panel" className={css(styles.panelWrapper)}>
                  <div className={css(styles.paneHeader)}>Plan</div>
                  <div className={css(styles.scrollableContent)}>
                    <planArtifact.Component data={planArtifact.data} editable={isEditable} />
                  </div>
                </div>
              </Panel>
              <PanelResizeHandle
                className={css(styles.resizer, isPlanResizerDragging && styles.resizerActive)}
                onDragging={setIsPlanResizerDragging}
              >
                <div aria-hidden="true" className={css(styles.resizerWrapper)}>
                  <div className={css(styles.resizerHandle)} />
                </div>
              </PanelResizeHandle>
            </>
          )}

          {/* Chat panel */}
          <Panel id="chat" order={1} defaultSize={chatSize} minSize={20} className={css(styles.panel)}>
            <div role="region" aria-label="Chat panel" className={css(styles.panelWrapper)}>
              <div className={css(styles.scrollableContent)}>{chatContent}</div>
            </div>
          </Panel>

          {/* Preview panel */}
          {hasPreview && (
            <>
              <PanelResizeHandle
                className={css(styles.resizer, isPreviewResizerDragging && styles.resizerActive)}
                onDragging={setIsPreviewResizerDragging}
              >
                <div aria-hidden="true" className={css(styles.resizerWrapper)}>
                  <div className={css(styles.resizerHandle)} />
                </div>
              </PanelResizeHandle>
              <Panel
                id="preview"
                order={2}
                defaultSize={previewSize}
                minSize={15}
                maxSize={60}
                className={css(styles.panel)}
              >
                <div role="region" aria-label="Preview panel" className={css(styles.panelWrapper)}>
                  <PreviewPane content={previewContent} onClose={closePreview} />
                </div>
              </Panel>
            </>
          )}
        </PanelGroup>
      </div>
    </AnalysisWorkspaceContext.Provider>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flex: 1,
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  panel: {
    height: '100%',
  },
  panelWrapper: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: WHITE,
  },
  paneHeader: {
    padding: '12px 16px',
    fontSize: 14,
    fontWeight: 600,
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    flexShrink: 0,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  resizer: {
    width: '12px',
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    position: 'relative',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.15s ease',
    ':hover': {
      backgroundColor: `rgba(${hexToRgba(GRAY_LIGHT, 0.3)})`,
    },
    boxShadow: `2px 0 8px 0 ${GRAY}, inset -1px 0 0 0 ${GRAY_LIGHT}`,
  },
  resizerWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  resizerActive: {
    backgroundColor: `rgba(${hexToRgba(GRAY_LIGHT, 0.5)})`,
  },
  resizerHandle: {
    width: '2px',
    height: '40px',
    backgroundColor: GRAY_LIGHT,
    borderRadius: '2px',
    transition: 'all 0.15s ease',
  },
});
